import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Must mirror the backend's INACTIVITY_TIMEOUT_MINUTES (see /app/backend/.env). This component
// is UX only - the server independently enforces the real timeout regardless of this countdown.
const TIMEOUT_MINUTES = 15;
const WARNING_BEFORE_MINUTES = 2;
const HEARTBEAT_MIN_INTERVAL_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 5000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

export function IdleSessionGuard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const lastHeartbeatRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    lastActivityRef.current = Date.now();

    // Only genuine mouse/keyboard/touch activity resets the clock and pings the server - never a
    // timer, a background poll, or a health check. That is what makes 15 minutes mean 15 real
    // idle minutes.
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      const now = Date.now();
      if (now - lastHeartbeatRef.current > HEARTBEAT_MIN_INTERVAL_MS) {
        lastHeartbeatRef.current = now;
        api.post("/auth/heartbeat").catch(() => {});
      }
      setShowWarning(false);
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    const interval = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const timeoutMs = TIMEOUT_MINUTES * 60 * 1000;
      const warnAtMs = (TIMEOUT_MINUTES - WARNING_BEFORE_MINUTES) * 60 * 1000;
      if (idleMs >= timeoutMs) {
        setShowWarning(false);
        logout().finally(() => navigate("/login"));
      } else if (idleMs >= warnAtMs) {
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.round((timeoutMs - idleMs) / 1000)));
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      clearInterval(interval);
    };
  }, [user, logout, navigate]);

  const staySignedIn = async () => {
    lastActivityRef.current = Date.now();
    lastHeartbeatRef.current = Date.now();
    setShowWarning(false);
    try {
      await api.post("/auth/heartbeat");
    } catch (e) {
      // server is authoritative - if the heartbeat itself 401s, the next request will redirect to login
    }
  };

  if (!user) return null;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent data-testid="idle-timeout-dialog" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>You've been idle</DialogTitle>
          <DialogDescription>
            For your security, you'll be signed out in <span data-testid="idle-seconds-left" className="font-semibold text-jigsaw-navy">{secondsLeft}s</span> due to
            inactivity. This is a UX warning only - the server enforces the real 15-minute idle timeout independently.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button data-testid="idle-stay-signed-in-button" onClick={staySignedIn} className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark">
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
